// Silent access-token refresh (AUTHZ-019).
//
// Access tokens are short-lived. When one expires the API returns 401; we transparently
// exchange it for a fresh token via /login/refreshToken and retry the original request once.
// A single in-flight refresh is shared across every axios instance and every concurrent
// request, so a burst of 401s triggers exactly one refresh. If the refresh fails — the
// session passed its absolute lifetime, it's an impersonation session, or there's no token —
// we clear the session and route the user to login.
import axios from "axios";
import { baseURL, uiVersion } from "../config";
import {
  getSessionStorage,
  setSessionStorage,
} from "../../infrastructure/utils/helpers";

let refreshInFlight = null;

// Don't try to refresh a 401 that came from the auth endpoints themselves (those 401s are
// genuine credential failures, not an expired session — refreshing would loop).
const isAuthEndpoint = (url = "") =>
  /\/login(\/|\?|$)|\/getOtp|\/verifyOtp|\/refreshToken/.test(url);

export const refreshAccessToken = () => {
  if (refreshInFlight) return refreshInFlight;
  refreshInFlight = (async () => {
    const current = getSessionStorage("token");
    if (!current) throw new Error("no token to refresh");
    // Send the raw { token } body (the API Gateway wraps it into body-json) AND the current
    // token as the auth header — /login/refreshToken sits behind the gateway authorizer, so
    // the header must carry a still-valid token. This is why we refresh PROACTIVELY (below),
    // before expiry, rather than only reacting to a 401 (an already-expired token may be
    // rejected at the gateway before the app can exchange it).
    const resp = await axios.post(
      `${baseURL}api/v1/login/refreshToken`,
      { token: current },
      {
        params: { uiversion: uiVersion },
        headers: { authorization: current, Authorization: current },
      }
    );
    const newToken = resp && resp.data && resp.data.status ? resp.data.data : null;
    if (!newToken || typeof newToken !== "string") {
      throw new Error("refresh did not return a token");
    }
    setSessionStorage("token", newToken);
    return newToken;
  })().finally(() => {
    refreshInFlight = null;
  });
  return refreshInFlight;
};

// Read a JWT's `exp` (ms) without verifying — just to time the proactive refresh.
const getTokenExpMs = (token) => {
  try {
    const payload = JSON.parse(atob(String(token).split(".")[1]));
    return payload && payload.exp ? payload.exp * 1000 : null;
  } catch (e) {
    return null;
  }
};

// Proactive refresh: while the app is open, renew the token a few minutes BEFORE it expires
// (so the still-valid token passes the gateway authorizer). A light 60s poll avoids
// per-token timer bookkeeping and naturally picks up a token that appears after login.
const REFRESH_SKEW_MS = 5 * 60 * 1000; // refresh when < 5 min of life remains
let proactiveStarted = false;

export const startProactiveRefresh = () => {
  if (proactiveStarted || typeof window === "undefined") return;
  proactiveStarted = true;
  setInterval(async () => {
    const token = getSessionStorage("token");
    if (!token) return;
    // Impersonation sessions are short and non-refreshable — leave them alone.
    if (getSessionStorage("impersonationToken")) return;
    const expMs = getTokenExpMs(token);
    if (!expMs) return;
    if (expMs - Date.now() < REFRESH_SKEW_MS) {
      try {
        await refreshAccessToken();
      } catch (e) {
        // Leave recovery to the reactive interceptor / next request (which logs out).
      }
    }
  }, 60 * 1000);
};

const forceLogout = () => {
  try {
    localStorage.clear();
  } finally {
    // Send to the app root (login). Hard nav so no stale in-memory state survives.
    window.location.href = "/";
  }
};

// Attach the 401 -> refresh -> retry-once behaviour to an axios instance.
export const installAuthRefreshInterceptor = (instance) => {
  instance.interceptors.response.use(
    (response) => response,
    async (error) => {
      const original = (error && error.config) || {};
      const status = error && error.response && error.response.status;
      if (
        status === 401 &&
        !original._retried &&
        !isAuthEndpoint(original.url || "")
      ) {
        original._retried = true;
        // Impersonation sessions are deliberately short and NON-refreshable — a 401 means the
        // impersonation window ended, so end the session rather than refresh the base token.
        if (getSessionStorage("impersonationToken")) {
          forceLogout();
          return Promise.reject(error);
        }
        try {
          const newToken = await refreshAccessToken();
          original.headers = original.headers || {};
          original.headers.authorization = newToken;
          return instance(original);
        } catch (e) {
          forceLogout();
          return Promise.reject(error);
        }
      }
      return Promise.reject(error);
    }
  );
};
